import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Boxes, Plus, Pencil, Trash2, Layers, ListChecks, FolderTree, Tags, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { useTerminology } from "@/lib/terminology";
import { useTabTitle } from "@/lib/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { toast } from "sonner";
import { PageHeader, PageBody, EmptyState } from "@/components/PageChrome";
import { slugifyKey as slugify } from "@/lib/slugify";

export default function EntityTypesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [form, setForm] = useState({
    name_singular: "",
    name_plural: "",
    key: "",
    description: "",
  });
  const [keyTouched, setKeyTouched] = useState(false);
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTerminology();
  const newCollectionLabel = t("collection.new");
  useTabTitle(t("nav.data") || "Collections", "boxes");

  // Sidebar "+ Add new Collection" navigates here with ?new=1 to auto-open the
  // dialog in create mode.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditingId(null);
      setForm({ name_singular: "", name_plural: "", key: "", description: "" });
      setKeyTouched(false);
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = async () => {
    try {
      const r = await api.get("/entity-types");
      setItems(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm({ name_singular: "", name_plural: "", key: "", description: "" });
    setKeyTouched(false);
    setEditingId(null);
  };

  const openForCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openForEdit = (et) => {
    setForm({
      name_singular: et.name_singular || "",
      name_plural: et.name_plural || "",
      key: et.key || "",
      description: et.description || "",
    });
    setKeyTouched(true); // key is immutable when editing
    setEditingId(et.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      if (editingId) {
        // key is immutable — send only the editable attributes.
        await api.patch(`/entity-types/${editingId}`, {
          name_singular: form.name_singular,
          name_plural: form.name_plural,
          description: form.description || null,
        });
        toast.success(t("collection.updated_toast", { name: form.name_singular }) || `${form.name_singular} updated`);
      } else {
        await api.post("/entity-types", form);
        toast.success(t("collection.created_toast", { name: form.name_singular }));
      }
      setOpen(false);
      resetForm();
      await load();
      // Tell the sidebar (and anything else) its collection list is stale.
      window.dispatchEvent(new Event("ubos:collections-changed"));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (et) => {
    if (!window.confirm(`Delete "${et.name_plural}"?\nAll ${et.name_plural} and their fields will be removed. This can't be undone.`))
      return;
    try {
      await api.delete(`/entity-types/${et.id}`);
      toast.success(t("collection.deleted_toast", { collectionName: et.name_singular || et.name_plural }));
      await load();
      window.dispatchEvent(new Event("ubos:collections-changed"));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Collections"
        subtitle={`All your ${(t("collection.plural") || "Collections").toLowerCase()}.`}
        breadcrumbs={[{ label: "UBOS" }, { label: "Collections" }]}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <Button onClick={openForCreate} data-testid="new-entity-type-btn">
              <Plus className="w-4 h-4 mr-1.5" /> {newCollectionLabel}
            </Button>
            <DialogContent className="sm:max-w-lg" data-testid="entity-type-dialog">
              <form onSubmit={submit}>
                <DialogHeader>
                  <DialogTitle>{editingId ? `Edit ${t("collection.singular") || "Collection"}` : newCollectionLabel}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="name_singular">Singular name</Label>
                      <Input
                        id="name_singular"
                        data-testid="input-name-singular"
                        value={form.name_singular}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((f) => ({
                            ...f,
                            name_singular: v,
                            key: keyTouched ? f.key : slugify(v),
                          }));
                        }}
                        placeholder="Product"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="name_plural">Plural name</Label>
                      <Input
                        id="name_plural"
                        data-testid="input-name-plural"
                        value={form.name_plural}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, name_plural: e.target.value }))
                        }
                        placeholder="Products"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="key">
                      Key{" "}
                      <span className="font-mono text-muted-foreground text-xs">
                        {editingId ? "(can't change)" : "(a-z, 0-9, _)"}
                      </span>
                    </Label>
                    <Input
                      id="key"
                      data-testid="input-key"
                      value={form.key}
                      disabled={!!editingId}
                      onChange={(e) => {
                        setKeyTouched(true);
                        setForm((f) => ({ ...f, key: slugify(e.target.value) }));
                      }}
                      placeholder="products"
                      required
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="input-description"
                      value={form.description}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, description: e.target.value }))
                      }
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="submit-entity-type">
                    {creating
                      ? (editingId ? "Saving…" : "Creating…")
                      : (editingId ? "Save changes" : "Create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title={`No ${(t("collection.plural") || "Collections").toLowerCase()} yet`}
            description={`Add your first ${t("collection.singular") || "Collection"} to start tracking the things your business cares about.`}
            action={
              <div className="flex gap-2">
                <Button onClick={() => setOpen(true)} data-testid="empty-new-entity-type">
                  <Plus className="w-4 h-4 mr-1.5" /> {newCollectionLabel}
                </Button>
              </div>
            }
            testId="entity-types-empty"
          />
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            data-testid="entity-types-grid"
          >
            {items.map((et) => (
              <Card
                key={et.id}
                className="group border-border hover:border-primary/40 transition-colors"
                data-testid={`entity-card-${et.key}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="truncate">{et.name_plural}</CardTitle>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openForEdit(et)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 text-muted-foreground hover:text-primary"
                        data-testid={`edit-entity-${et.key}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(et)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-entity-${et.key}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-3 min-h-[42px]">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {et.description || (
                      <span className="italic opacity-70">No description</span>
                    )}
                  </p>
                </CardContent>
                <CardFooter className="flex flex-wrap gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => nav(`/entity-types/${et.id}/fields`)}
                    data-testid={`fields-btn-${et.key}`}
                  >
                    <Layers className="w-3.5 h-3.5 mr-1.5" /> Fields
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => nav(`/entity-types/${et.id}/categories`)}
                    data-testid={`categories-btn-${et.key}`}
                  >
                    <FolderTree className="w-3.5 h-3.5 mr-1.5" /> Categories
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => nav(`/entity-types/${et.id}/tags`)}
                    data-testid={`tags-btn-${et.key}`}
                  >
                    <Tags className="w-3.5 h-3.5 mr-1.5" /> Tags
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => nav(`/entity-types/${et.id}/relationships`)}
                    data-testid={`rels-btn-${et.key}`}
                  >
                    <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Links
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => nav(`/entity-types/${et.id}/records`)}
                    data-testid={`records-btn-${et.key}`}
                  >
                    <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Open
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </PageBody>
    </>
  );
}
